import React, { useEffect, useState } from "react";
import moment from "moment";
import { formatADtoBEString } from "react-lib/utils/dateUtils";
import CardServiceFormUX from "./CardServiceFormUX";
import {
  FieldSchema,
  Metadata,
  SelectOption,
  childMetadata,
  getFields,
  getKindSchema,
  pricingFields,
  editPricingFields,
  serviceMetadata,
  isFieldEnabled,
} from "./serviceKindSchema";
import {
  ActionParams,
  CatalogForm,
  RecordData,
  isoDate,
  buildServicePayload,
  pricingValues,
  buildPricingPatch,
  pricingStatus,
  todayISO,
  formatServiceEndDate,
} from "./sequence/ServiceCatalog";

type Props = {
  form: CatalogForm;
  masters: Record<string, SelectOption[]>;
  masterErrors?: Record<string, string>;
  loading: boolean;
  canCreatePackage?: boolean;
  runSequence: (params: ActionParams) => any;
  setProp: (key: string, value: any) => any;
};
export const validateFields = (
  fields: FieldSchema[],
  metadata: Metadata,
  values: RecordData
): RecordData => {
  const errors: RecordData = {};
  fields.forEach((field) => {
    const meta = metadata[field.key];
    if (!meta || !isFieldEnabled(field) || field.readOnly || field.uiOnly || meta.read_only) return;
    const value = values[field.key];
    if (
      meta.required &&
      (value === undefined ||
        value === null ||
        value === "" ||
        (Array.isArray(value) && !value.length))
    ) {
      errors[field.key] = ["กรุณาระบุข้อมูล"];
    } else if (value !== undefined && value !== null && value !== "") {
      if (meta.max_length && String(value).length > meta.max_length)
        errors[field.key] = [`ระบุได้ไม่เกิน ${meta.max_length} ตัวอักษร`];
      if (field.input === "date" && !moment(isoDate(value), "YYYY-MM-DD", true).isValid())
        errors[field.key] = ["วันที่ไม่ถูกต้อง"];
      if (["number", "decimal"].includes(field.input) && !Number.isFinite(Number(value)))
        errors[field.key] = ["กรุณาระบุตัวเลข"];
      if (field.input === "decimal" && Number.isFinite(Number(value))) {
        const decimal = String(value).replace(/^[+-]/, "");
        const [whole, fraction = ""] = decimal.split(".");
        if (/^\d*(\.\d*)?$/.test(decimal)) {
          const wholeDigits = whole.replace(/^0+/, "").length;
          if (meta.decimal_places != null && fraction.length > meta.decimal_places)
            errors[field.key] = [`ระบุทศนิยมได้ไม่เกิน ${meta.decimal_places} ตำแหน่ง`];
          if (
            meta.max_digits != null &&
            (wholeDigits + fraction.length > meta.max_digits ||
              (meta.decimal_places != null && wholeDigits > meta.max_digits - meta.decimal_places))
          )
            errors[field.key] = ["จำนวนหลักเกินที่กำหนด"];
        }
      }
      if (field.input === "textList" && Array.isArray(value)) {
        const maxLength = meta.child?.max_length;
        const rows = value.map((item) =>
          maxLength && String(item).trim().length > maxLength
            ? [`ระบุได้ไม่เกิน ${maxLength} ตัวอักษร`]
            : null
        );
        if (rows.some(Boolean)) errors[field.key] = rows;
      }
      if (field.fields && Array.isArray(value)) {
        const rows = value.map((row) => validateFields(field.fields!, childMetadata(meta), row));
        if (rows.some((row) => Object.keys(row).length)) errors[field.key] = rows;
      }
    }
  });
  return errors;
};
export const validateServiceValues = (form: CatalogForm, values: RecordData): RecordData => {
  const metadata = serviceMetadata(form.metadata, values, !form.original);
  const fields = getFields(form.kind, metadata);
  const changed = form.original
    ? buildServicePayload({ ...form, metadata }, values, [], true)
    : values;
  const errors = validateFields(
    fields.filter(
      (field) => !form.original || Object.prototype.hasOwnProperty.call(changed, field.key)
    ),
    metadata,
    changed
  );
  if (values.is_child_lab && metadata.parent_lab && !values.parent_lab)
    errors.parent_lab = ["กรุณาเลือก Lab แม่"];
  return errors;
};
const CardServiceForm = (props: Props) => {
  const { form } = props;
  const [values, setValues] = useState<RecordData>(form.values);
  const [pricingRows, setPricingRows] = useState<RecordData[]>([{}]);
  const [newPricing, setNewPricing] = useState<RecordData>({});
  const [noPrice, setNoPrice] = useState(false);
  const [image, setImage] = useState<File | null>(null);
  const [localErrors, setLocalErrors] = useState<RecordData>({});
  const selectedPricing = form.pricings.find((row) => row.id === form.selectedPricing);
  const baselinePricing = JSON.stringify(pricingValues(selectedPricing));
  const [editPricing, setEditPricing] = useState<RecordData>(() => pricingValues(selectedPricing));
  const [localPricingErrors, setLocalPricingErrors] = useState<RecordData>({});
  useEffect(() => {
    setEditPricing(JSON.parse(baselinePricing));
    setLocalPricingErrors({});
  }, [selectedPricing?.id, baselinePricing]);
  const pricingPatch = selectedPricing
    ? buildPricingPatch(selectedPricing, editPricing, form.pricingMetadata)
    : {};
  const pricingDirty = !!Object.keys(pricingPatch).length;
  const pricingPanelMetadata = { ...form.pricingMetadata };
  ["bill_mode", "start_date", "end_date", "price_normal"].forEach((key) => {
    pricingPanelMetadata[key] = { ...pricingPanelMetadata[key], required: true };
  });
  const editPricingMetadata = { ...pricingPanelMetadata };
  if (selectedPricing && pricingStatus(selectedPricing) === "ใช้อยู่")
    editPricingMetadata.start_date = { ...editPricingMetadata.start_date, read_only: true };
  const pricingPeriod = (row: RecordData) =>
    `${formatADtoBEString(row.start_date)} – ${formatServiceEndDate(row.end_date)}`;

  const metadata = serviceMetadata(form.metadata, values, !form.original);
  const fields = getFields(form.kind, metadata);
  const errors = { ...form.errors, ...localErrors };
  const change = (key: string, value: any) => {
    setValues((prev) => ({
      ...prev,
      [key]: value,
      ...(key === "is_child_lab" && !value && { parent_lab: null }),
    }));
    setLocalErrors({});
    props.setProp("ServiceCatalogSequence.form.errors", {});
  };
  const run = (action: ActionParams["action"], params: Partial<ActionParams> = {}) =>
    props.runSequence({ sequence: "ServiceCatalog", action, ...params });
  const onSave = () => {
    if (selectedPricing && pricingDirty) {
      const validation = validateFields(editPricingFields, editPricingMetadata, editPricing);
      if (
        !validation.end_date &&
        editPricing.end_date &&
        pricingStatus(selectedPricing) === "ใช้อยู่" &&
        isoDate(editPricing.end_date) < todayISO()
      )
        validation.end_date = ["วันที่สิ้นสุดต้องไม่ก่อนวันนี้"];
      if (
        !validation.start_date &&
        editPricing.start_date &&
        pricingStatus(selectedPricing) === "ล่วงหน้า" &&
        isoDate(editPricing.start_date) <= todayISO()
      )
        validation.start_date = ["วันที่เริ่มต้นของช่วงราคาล่วงหน้าต้องหลังวันนี้"];
      setLocalPricingErrors(validation);
      if (Object.keys(validation).length) {
        props.setProp("ServiceCatalogSequence.form.tab", 1);
        props.setProp("ServiceCatalogSequence.form.addingPricing", false);
        return;
      }
    }
    const validation = validateServiceValues(form, values);
    if (!form.original && !noPrice) {
      const rows = pricingRows.map((row) =>
        validateFields(pricingFields, form.pricingMetadata, row)
      );
      if (rows.some((row) => Object.keys(row).length)) validation.full_pricings = rows;
    }
    setLocalErrors(validation);
    if (Object.keys(validation).length) {
      props.setProp(
        "ServiceCatalogSequence.form.tab",
        Object.keys(validation).some((key) => key !== "full_pricings") ? 0 : 1
      );
      return;
    }
    run("SAVE_SERVICE", {
      values,
      pricings: pricingRows,
      noPrice,
      image,
      ...(selectedPricing &&
        pricingDirty && { editingPricing: { id: selectedPricing.id, values: editPricing } }),
    });
  };
  const onAddPricing = () => {
    const validation = validateFields(
      form.original ? editPricingFields : pricingFields,
      form.original ? pricingPanelMetadata : form.pricingMetadata,
      newPricing
    );
    setLocalErrors(validation);
    if (!Object.keys(validation).length) run("ADD_PRICING", { values: newPricing });
  };
  return (
    <CardServiceFormUX
      form={{ ...form, metadata }}
      canCreatePackage={props.canCreatePackage}
      label={getKindSchema(form.kind)?.label || form.kind}
      fields={fields}
      pricingFields={pricingFields}
      masters={props.masters}
      masterErrors={props.masterErrors}
      values={values}
      errors={errors}
      pricingRows={pricingRows}
      newPricing={newPricing}
      noPrice={noPrice}
      loading={props.loading || !!form.pricingBusy}
      editPricing={editPricing}
      editPricingMetadata={editPricingMetadata}
      newPricingMetadata={pricingPanelMetadata}
      editPricingErrors={{ ...form.pricingErrors, ...localPricingErrors }}
      onEditPricing={(key, value) => {
        setEditPricing((previous) => ({ ...previous, [key]: value }));
        setLocalPricingErrors({});
        props.setProp("ServiceCatalogSequence.form.pricingErrors", {});
      }}
      onCopyPricing={(row) => {
        if (
          form.addingPricing &&
          Object.values(newPricing).some((value) => value !== "" && value != null) &&
          !window.confirm("ข้อมูลราคาใหม่ที่กรอกจะไม่ถูกบันทึก ต้องการคัดลอกช่วงราคาแทนหรือไม่")
        )
          return;
        const copied = pricingValues(row);
        setNewPricing({ ...copied, start_date: "", end_date: "" });
        setLocalErrors({});
        props.setProp("ServiceCatalogSequence.form.errors", {});
        props.setProp("ServiceCatalogSequence.form.addingPricing", true);
      }}
      onDeletePricing={(row) => {
        if (window.confirm(`ลบช่วงราคา ${pricingPeriod(row)} ใช่หรือไม่`))
          run("DELETE_PRICING", { pricingId: row.id });
      }}
      onChange={change}
      onImage={setImage}
      onPricingRows={(rows) => {
        setPricingRows(rows);
        setLocalErrors({});
      }}
      onNoPrice={(checked) => {
        setNoPrice(checked);
        setLocalErrors({});
        props.setProp("ServiceCatalogSequence.form.errors", {});
      }}
      onNewPricing={(key, value) => {
        setNewPricing((prev) => ({ ...prev, [key]: value }));
        setLocalErrors({});
        props.setProp("ServiceCatalogSequence.form.errors", {});
      }}
      onTab={(tab) => props.setProp("ServiceCatalogSequence.form.tab", tab)}
      onTogglePricing={(adding) => {
        setLocalErrors({});
        setNewPricing({});
        props.setProp("ServiceCatalogSequence.form.addingPricing", adding);
        props.setProp("ServiceCatalogSequence.form.errors", {});
      }}
      onSelectPricing={(id) => {
        if (id === form.selectedPricing) return;
        if (
          pricingDirty &&
          !window.confirm("ข้อมูลช่วงราคาที่แก้ไขจะไม่ถูกบันทึก ต้องการเปลี่ยนหรือไม่")
        )
          return;
        props.setProp("ServiceCatalogSequence.form.pricingErrors", {});
        props.setProp("ServiceCatalogSequence.form.selectedPricing", id);
      }}
      onClose={() => {
        const dirty =
          JSON.stringify(values) !== JSON.stringify(form.values) ||
          JSON.stringify(pricingRows) !== JSON.stringify([{}]) ||
          Object.keys(newPricing).length > 0 ||
          noPrice ||
          !!image ||
          pricingDirty;
        if (
          !form.readOnly &&
          dirty &&
          !window.confirm("ข้อมูลที่แก้ไขจะไม่ถูกบันทึก ต้องการปิดหรือไม่")
        )
          return;
        run("CLOSE_SERVICE_FORM");
      }}
      onSave={onSave}
      onAddPricing={onAddPricing}
      onRemoteSearch={(field, search) => run("SEARCH_RELATED_SERVICES", { field, search })}
    />
  );
};
export default CardServiceForm;
